import React, { useState, useEffect, useCallback } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { Box, Grid, Typography, Button, LinearProgress, FormControlLabel } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import Spin from 'components/atoms/Spin/Spin';
import FormikTextField from 'components/atoms/FormikInput/FormikInput';
import { initRegion, RegionDto } from 'api/regions';
import { useDictionaryApi } from '../../api/dictionary';
import { FormikSwitch } from '../../components/atoms/FormikSwitch/FormikSwitch';

let myMap: any;

const RegionDetails = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const params = useParams<Params>();
    const [loading, setLoading] = useState(false);
    const [item, setItem] = useState<RegionDto>(initRegion());
    const [id, setId] = useState<string | undefined>();
    const { post, put, getById } = useDictionaryApi<RegionDto>('/regions');
    const [coords, setCoords] = useState<number[] | undefined>(undefined);
    const [loadMap, setLoadMap] = useState<boolean>(false);

    const handleBack = () => {
        history.goBack();
    };

    const schema = () =>
        Yup.object().shape({
            code: Yup.string().required('Введите код'),
            description: Yup.string().required('Введите наименование'),
        });

    const handleLoad = useCallback(() => {
        const { ymaps } = window as any;
        let myPlacemark: any;

        ymaps.ready(() => {
            setLoadMap(false);
            myMap = new ymaps.Map(
                'map',
                {
                    center: coords && coords.length > 0 ? coords : [51.128207, 71.430411],
                    zoom: 11,
                },
                {
                    balloonMaxWidth: 200,
                    provider: 'yandex#map',
                }
            );

            if (coords && coords.length > 0) {
                insertMark(coords);
            }

            myMap.events.add('click', (e: any) => {
                const localCoords = e.get('coords');
                insertMark(localCoords);
                // Если метка уже создана – просто передвигаем ее.

                getAddress(localCoords);
            });

            function insertMark(localCoords: any) {
                if (myPlacemark) {
                    myPlacemark.geometry.setCoordinates(localCoords);
                }
                // Если нет – создаем.
                else {
                    myPlacemark = createPlacemark(localCoords);
                    myMap.geoObjects.add(myPlacemark);
                    // Слушаем событие окончания перетаскивания на метке.
                    myPlacemark.events.add('dragend', () => {
                        getAddress(myPlacemark.geometry.getCoordinates());
                    });
                }
            }

            function getAddress(localCoords: any) {
                myPlacemark.properties.set('iconCaption', 'Поиск...');
                const roundedCoords = [
                    localCoords[0].toPrecision(6),
                    localCoords[1].toPrecision(6),
                ];
                setCoords(roundedCoords);
                myPlacemark.properties.set({
                    // Формируем строку с данными об объекте.
                    iconCaption: roundedCoords.join(', '),
                    balloonContent: '',
                });
            }

            // Создание метки.
            function createPlacemark(coords: any) {
                return new ymaps.Placemark(
                    coords,
                    {
                        iconCaption: coords.join(', '),
                    },
                    {
                        preset: 'islands#violetDotIconWithCaption',
                        draggable: true,
                    }
                );
            }
        });
    }, [coords]);

    useEffect(() => {
        setId(params.id);
        if (params.id !== undefined) {
            setLoading(true);
            getById(params.id).then((res) => {
                setItem(res);
                if (res.latitude && res.longitude) {
                    setCoords([Number(res.latitude), Number(res.longitude)]);
                }

                setLoadMap(true);
                setLoading(false);
            });
        } else {
            setLoadMap(true);
        }
    }, [getById, params.id]);

    useEffect(() => {
        if (loadMap) {
            handleLoad();
        }
    }, [handleLoad, loadMap]);

    return (
        <Box position="relative">
            <Formik
                enableReinitialize={true}
                initialValues={item}
                validationSchema={schema()}
                onSubmit={(values, { setSubmitting }) => {
                    if (item === undefined) {
                        return;
                    }

                    const sendData = {
                        ...values,
                        ...{
                            latitude: coords && coords.length > 0 ? coords[0] : 0,
                            longitude: coords && coords.length > 1 ? coords[1] : 0,
                        },
                    };

                    if (id === undefined) {
                        post(sendData)
                            .then(() => {
                                setSubmitting(false);
                                handleBack();
                            })
                            .catch(() => {
                                enqueueSnackbar('Ошибка при сохранении данных', {
                                    variant: 'error',
                                });
                            })
                            .finally(() => setSubmitting(false));
                    } else {
                        put({ ...item, ...sendData })
                            .then(() => {
                                handleBack();
                            })
                            .catch(() => {
                                enqueueSnackbar('Ошибка при сохранении данных', {
                                    variant: 'error',
                                });
                            })
                            .finally(() => setSubmitting(false));
                    }
                }}
            >
                {({ isSubmitting, submitForm }) => (
                    <Form className={classes.root}>
                        <Grid
                            className={classes.container}
                            container
                            item
                            spacing={4}
                            xs={12}
                            md={12}
                            xl={10}
                            lg={10}
                        >
                            {loading && <Spin />}
                            <Typography variant="h3" className={classes.title}>
                                {id ? 'Редактирование' : 'Добавление'} региона
                            </Typography>
                            <Grid item xs={6}>
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="code"
                                    label="Код"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="index"
                                    label="Индекс региона"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="description"
                                    label="Наименование"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Field
                                    component={FormikTextField}
                                    className={classes.mt5}
                                    name="taptophone_code"
                                    label="taptophone"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <FormControlLabel
                                    className={classes.isOpened}
                                    control={
                                        <Field
                                            component={FormikSwitch}
                                            name="is_show_freelancer"
                                            type="checkbox"
                                            color="primary"
                                        />
                                    }
                                    label="Показать внештатным курьерам"
                                />
                            </Grid>

                            <Grid container>
                                <div id="map" className={classes.mapContainer}></div>
                            </Grid>

                            <div className={classes.progress}>
                                {isSubmitting && <LinearProgress />}
                            </div>
                            <Grid item container xs={12} justify="center" spacing={2}>
                                <Button
                                    disabled={isSubmitting}
                                    onClick={submitForm}
                                    variant="contained"
                                    color="primary"
                                >
                                    Сохранить
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleBack}
                                    style={{ marginLeft: '10px' }}
                                >
                                    Отмена
                                </Button>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

interface Params {
    id: string | undefined;
}

const useStyles = makeStyles((theme) => ({
    root: {},
    mt5: {
        marginTop: theme.spacing(3),
        width: '100%',
    },
    progress: {
        height: '4px',
        width: '100%',
    },
    container: {
        position: 'relative',
    },
    title: {
        width: '100%',
        paddingLeft: theme.spacing(2),
    },
    mapContainer: {
        width: '70%',
        margin: '0 auto',
        height: '400px',
        border: '1px solid #ccc',
    },
    isOpened: {
        marginTop: theme.spacing(3),
    },
}));

export default RegionDetails;
